import { useState, useEffect } from 'react';
import {
  Loader2, RefreshCw, AlertTriangle, CheckCircle2, Link, MapPin,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { diagnosticsAPI, smoobuAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

// ── Unmapped Landlords Section ──────────────────────────────────────────────

function UnmappedLandlordsSection() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [selections, setSelections] = useState({});   // norm -> slug
  const [submitting, setSubmitting] = useState({});    // norm -> bool
  const [rowResults, setRowResults] = useState({});    // norm -> result

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setRowResults({});
    try {
      const { data: d } = await diagnosticsAPI.unmappedStatementLandlords();
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleAssign = async (item, override = false) => {
    const norm = item.parsed_landlord_name_normalised;
    const entitySlug = selections[norm];
    if (!entitySlug || !item.sample_statement_id) return;

    setSubmitting(prev => ({ ...prev, [norm]: true }));
    setRowResults(prev => ({ ...prev, [norm]: null }));

    try {
      const { data: result } = await diagnosticsAPI.assignStatementLandlord({
        statement_id: item.sample_statement_id,
        entity_slug: entitySlug,
        override,
      });
      setRowResults(prev => ({ ...prev, [norm]: result }));
      if (result.status === 'created') {
        setTimeout(loadData, 500);
      }
    } catch (err) {
      setRowResults(prev => ({
        ...prev,
        [norm]: { status: 'error', detail: getErrorMessage(err) },
      }));
    } finally {
      setSubmitting(prev => ({ ...prev, [norm]: false }));
    }
  };

  const isEmpty = data && data.total_unmapped_names === 0;
  const entities = data?.entity_options || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Unmapped Landlords</h3>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Statement landlord names that could not be resolved to a portfolio entity.
        Assign each to the correct entity to create a durable alias.
      </p>

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-500">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading unmapped landlords...
        </div>
      )}

      {isEmpty && (
        <div className="rounded-lg border p-4 text-center">
          <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
          <p className="text-sm text-muted-foreground">All landlords resolved.</p>
        </div>
      )}

      {data && data.total_unmapped_names > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Distinct unmapped names</p>
              <p className="text-xl font-bold tabular-nums">{data.total_unmapped_names}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Total unmapped statements</p>
              <p className="text-xl font-bold tabular-nums">{data.total_unmapped_statements}</p>
            </div>
          </div>

          <div className="space-y-3">
            {data.unmapped_landlords.map((item) => {
              const norm = item.parsed_landlord_name_normalised;
              const result = rowResults[norm];
              const isSubmitting = submitting[norm];

              return (
                <div key={norm} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium break-words">
                        {item.parsed_landlord_name || '(empty)'}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {item.statement_count} stmt{item.statement_count !== 1 ? 's' : ''}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {item.agency && <span>Agency: {item.agency}</span>}
                    {item.sample_property && <span>Property: {item.sample_property}</span>}
                    {item.months?.length > 0 && <span>Months: {item.months.join(', ')}</span>}
                  </div>

                  {item.sample_drive_link && (
                    <a
                      href={item.sample_drive_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Link className="h-3 w-3" />
                      View source PDF
                    </a>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <Select
                      value={selections[norm] || ''}
                      onValueChange={(val) =>
                        setSelections(prev => ({ ...prev, [norm]: val }))
                      }
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="h-8 text-xs flex-1">
                        <SelectValue placeholder="Select entity..." />
                      </SelectTrigger>
                      <SelectContent>
                        {entities.map((e) => (
                          <SelectItem key={e.slug} value={e.slug}>
                            <span className="truncate">
                              {e.canonical_name}
                              <span className="text-muted-foreground ml-1">({e.slug})</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      size="sm"
                      variant="default"
                      className="h-8 text-xs"
                      disabled={!selections[norm] || isSubmitting}
                      onClick={() => handleAssign(item)}
                    >
                      {isSubmitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                      Assign
                    </Button>
                  </div>

                  {result && result.status === 'created' && (
                    <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 rounded p-2">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      Assigned to {result.alias_created?.entity_name}.
                      Resolved {result.statements_resolved} statement{result.statements_resolved !== 1 ? 's' : ''}.
                    </div>
                  )}

                  {result && result.status === 'conflict' && (
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 text-xs text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 rounded p-2">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                          Conflict: already mapped to "{result.existing_entity_name}"
                          ({result.existing_entity_slug}).
                          New target: "{result.new_entity_name}" ({result.new_entity_slug}).
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 text-xs"
                        disabled={isSubmitting}
                        onClick={() => handleAssign(item, true)}
                      >
                        Override
                      </Button>
                    </div>
                  )}

                  {result && result.status === 'error' && (
                    <div className="flex items-start gap-2 text-xs text-red-500 bg-red-50 dark:bg-red-950/30 rounded p-2">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{result.detail}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Unmapped Properties Section ─────────────────────────────────────────────

function UnmappedPropertiesSection() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [properties, setProperties] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);

  const [selections, setSelections] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [rowResults, setRowResults] = useState({});

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setRowResults({});
    try {
      const { data: d } = await diagnosticsAPI.unmappedStatementProperties();
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    setPropsLoading(true);
    try {
      const { data: d } = await smoobuAPI.getProperties();
      const owned = (d.properties || []).filter(p => p.entity_id);
      owned.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProperties(owned);
    } catch (err) {
      console.error('Failed to load properties:', err);
    } finally {
      setPropsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadProperties();
  }, []);

  const handleMap = async (item, override = false) => {
    const norm = item.parsed_ref_normalised;
    const propertyId = selections[norm];
    if (!propertyId) return;

    setSubmitting(prev => ({ ...prev, [norm]: true }));
    setRowResults(prev => ({ ...prev, [norm]: null }));

    try {
      const { data: result } = await diagnosticsAPI.createStatementPropertyAlias({
        parsed_ref: item.parsed_ref,
        property_id: propertyId,
        override,
      });
      setRowResults(prev => ({ ...prev, [norm]: result }));
      if (result.status === 'created') {
        setTimeout(loadData, 500);
      }
    } catch (err) {
      setRowResults(prev => ({
        ...prev,
        [norm]: { status: 'error', detail: getErrorMessage(err) },
      }));
    } finally {
      setSubmitting(prev => ({ ...prev, [norm]: false }));
    }
  };

  const isEmpty = data && data.total_unmapped_refs === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Unmapped Properties</h3>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Statement property names that could not be matched to a Smoobu property.
        Map each ref to the correct property to create a durable alias.
      </p>

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-500">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading unmapped properties...
        </div>
      )}

      {isEmpty && (
        <div className="rounded-lg border p-4 text-center">
          <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
          <p className="text-sm text-muted-foreground">All statement properties are mapped.</p>
        </div>
      )}

      {data && data.total_unmapped_refs > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Distinct unmapped refs</p>
              <p className="text-xl font-bold tabular-nums">{data.total_unmapped_refs}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Total unmapped statements</p>
              <p className="text-xl font-bold tabular-nums">{data.total_unmapped_statements}</p>
            </div>
          </div>

          <div className="space-y-3">
            {data.unmapped_refs.map((item) => {
              const norm = item.parsed_ref_normalised;
              const result = rowResults[norm];
              const isSubmitting = submitting[norm];

              return (
                <div key={norm} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium break-words">{item.parsed_ref}</p>
                      {item.property_address_raw && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.property_address_raw}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {item.statement_count} stmt{item.statement_count !== 1 ? 's' : ''}
                      </Badge>
                      {item.match_source === 'ambiguous' && (
                        <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs">
                          ambiguous
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {item.agency && <span>Agency: {item.agency}</span>}
                    {item.landlord_entity && <span>Landlord: {item.landlord_entity}</span>}
                    {item.months?.length > 0 && <span>Months: {item.months.join(', ')}</span>}
                  </div>

                  {item.sample_booking && (
                    <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
                      Sample: {item.sample_booking.guest_name}
                      {item.sample_booking.arrival && ` (${item.sample_booking.arrival}`}
                      {item.sample_booking.departure && ` to ${item.sample_booking.departure})`}
                      {item.sample_booking.channel && ` via ${item.sample_booking.channel}`}
                      {item.sample_booking.gross && `, gross ${item.sample_booking.gross}`}
                    </div>
                  )}

                  {item.sample_drive_link && (
                    <a
                      href={item.sample_drive_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Link className="h-3 w-3" />
                      View source PDF
                    </a>
                  )}
                  {!item.sample_drive_link && item.sample_statement_id && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      Statement: {item.sample_statement_id}
                    </span>
                  )}

                  {item.candidates?.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Candidates: {item.candidates.map(c =>
                        `${c.name} (${c.smoobu_id})`
                      ).join('; ')}
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <Select
                      value={selections[norm] || ''}
                      onValueChange={(val) =>
                        setSelections(prev => ({ ...prev, [norm]: val }))
                      }
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="h-8 text-xs flex-1">
                        <SelectValue placeholder="Select property..." />
                      </SelectTrigger>
                      <SelectContent>
                        {propsLoading && (
                          <SelectItem value="_loading" disabled>
                            Loading...
                          </SelectItem>
                        )}
                        {properties.map((p) => (
                          <SelectItem key={p.smoobu_id} value={p.smoobu_id}>
                            <span className="truncate">
                              {p.name}
                              <span className="text-muted-foreground ml-1">
                                ({p.smoobu_id})
                                {p.entity_id && ` [${p.entity_id}]`}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      size="sm"
                      variant="default"
                      className="h-8 text-xs"
                      disabled={!selections[norm] || isSubmitting}
                      onClick={() => handleMap(item)}
                    >
                      {isSubmitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                      Map
                    </Button>
                  </div>

                  {result && result.status === 'created' && (
                    <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 rounded p-2">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      Mapped to {result.alias_created?.property_name}.
                      Rematched {result.statements_rematched} statement{result.statements_rematched !== 1 ? 's' : ''}.
                    </div>
                  )}

                  {result && result.status === 'conflict' && (
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 text-xs text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 rounded p-2">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                          Conflict: already mapped to "{result.existing_property_name}"
                          ({result.existing_property_id}).
                          New target: "{result.new_property_name}" ({result.new_property_id}).
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 text-xs"
                        disabled={isSubmitting}
                        onClick={() => handleMap(item, true)}
                      >
                        Override
                      </Button>
                    </div>
                  )}

                  {result && result.status === 'error' && (
                    <div className="flex items-start gap-2 text-xs text-red-500 bg-red-50 dark:bg-red-950/30 rounded p-2">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{result.detail}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Combined Card ───────────────────────────────────────────────────────────

export function StatementMappingRepairsPanel() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Statement Mapping Repairs</CardTitle>
        <p className="text-xs text-muted-foreground">
          Assign unmapped landlord names to portfolio entities and map unrecognised
          property names to Smoobu properties. Each assignment creates a durable alias
          replayed on future statements.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <UnmappedLandlordsSection />
        <div className="border-t" />
        <UnmappedPropertiesSection />
      </CardContent>
    </Card>
  );
}
